// drawer/components/EPSSComponent.tsx
import { EpssChart } from '@praetorian-chariot/ui';

import { PercentageChart } from '@/components/charts/PercentageChart';
import { ChartErrorBoundary, Section } from '@/sections/threats/components';
import type { EnrichedVulnerability } from '@/sections/threats/types';
import { getStatusForScore } from '@/sections/threats/utils';

interface EPSSComponentProps {
  threatIntel?: EnrichedVulnerability;
}

const EPSS_LEGEND = [
  {
    dotBg: 'bg-chariot-status-low',
    dotBorder: 'border-chariot-stroke-low',
    percent: '0-50%',
    label: 'Low (≤ 0.001)',
  },
  {
    dotBg: 'bg-chariot-status-medium',
    dotBorder: 'border-chariot-stroke-medium',
    percent: '50-90%',
    label: 'Medium (0.001-0.024)',
  },
  {
    dotBg: 'bg-chariot-status-high',
    dotBorder: 'border-chariot-stroke-high',
    percent: '90-95%',
    label: 'High (0.024-0.099)',
  },
  {
    dotBg: 'bg-chariot-status-critical',
    dotBorder: 'border-chariot-stroke-critical',
    percent: '95-99%',
    label: 'Critical (0.099-0.884)',
  },
  {
    dotBg: 'bg-chariot-status-severe',
    dotBorder: 'border-chariot-stroke-severe',
    percent: '99-100%',
    label: 'Severe (≥ 0.884)',
  },
];

/**
 * EPSS (Exploit Prediction Scoring System) display component
 */
export function EPSSComponent({ threatIntel }: EPSSComponentProps) {
  const { epss } = threatIntel ?? {};

  const score = Number(Math.min(epss?.score ?? 0, 1).toFixed(2));
  const percentile = Number(Math.min(epss?.percentile ?? 0, 1).toFixed(1));
  const percentage = percentile * 100;
  const percentileMeta = getStatusForScore(percentile, 10);

  return (
    <Section title="EPSS">
      {!epss && (
        <p className="text-sm">No information available at this time</p>
      )}
      {epss && (
        <div className="relative w-full overflow-hidden">
          {/* Legend + Percentile Row */}
          <div className="clearfix relative mb-8 w-full">
            {/* Legend */}
            <div
              className="bg-chariot-background-secondary float-left flex flex-col items-start gap-y-2 rounded-lg p-2 px-4"
              style={{ minWidth: 0, maxWidth: 'calc(100% - 200px)' }}
            >
              <div className="w-full">
                <span className="text-chariot-text-primary text-sm font-semibold">
                  EPSS Score Distribution
                </span>
              </div>
              <div className="flex flex-row flex-wrap gap-x-6 gap-y-2">
                {EPSS_LEGEND.map(item => (
                  <span
                    key={item.percent}
                    className="inline-flex flex-col items-start align-top"
                  >
                    <span className="mb-0.5 flex items-center gap-2">
                      <span
                        className={`size-[13px] rounded-full border ${item.dotBg} ${item.dotBorder}`}
                      />
                      <span className="text-chariot-text-primary text-xs leading-3 font-bold">
                        {item.percent}
                      </span>
                    </span>
                    <span className="text-chariot-text-primary text-xs leading-3 font-normal text-nowrap">
                      {item.label}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            {/* Percentile Card */}
            <div className="border-chariot-stroke bg-chariot-background-primary absolute top-0 right-8 z-20 flex w-[180px] flex-col items-center justify-center overflow-visible rounded-xl border p-0 sm:p-2">
              <div className="absolute top-14 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="flex size-[100px] items-center justify-center">
                  <PercentageChart
                    total={{ name: 'Total', value: 100 }}
                    current={{
                      name: 'EPSS',
                      value: percentage,
                      lineClassName: percentileMeta.lineClassName,
                    }}
                    doNotRoundCurrentValue
                    hidePercentage
                  />
                </div>
              </div>
              <div className="mt-24 flex flex-col items-center justify-center">
                <div className="text-chariot-text-primary mb-2 text-lg leading-5 font-bold">
                  Percentile
                </div>
                <div className="text-chariot-text-primary text-center text-base leading-6">
                  Likelihood of being exploited
                  <br />
                  compared to other vulnerabilities
                </div>
              </div>
            </div>
            <div className="clear-both" />
          </div>

          {/* Chart */}
          <div className="w-full max-w-full overflow-hidden">
            <ChartErrorBoundary>
              <EpssChart score={score} height={300} />
            </ChartErrorBoundary>
          </div>
        </div>
      )}
    </Section>
  );
}
